import axios from "axios";
import jwtDecode from "jwt-decode";

import { AUTH_SESSION_KEY, CLIENT_FILTER_KEY } from "common/utility";
import config from "../../config";

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;

    if (error && error.response && error.response.status === 404) {
      // window.location.href = '/not-found';
    } else if (error && error.response && error.response.status === 403) {
      window.location.href = "/access-denied";
    } else {
      switch (error.response.status) {
        case 401:
          message = "Invalid credentials";
          break;
        case 403:
          message = "Access Forbidden";
          break;
        case 404:
          message = "Sorry! the data you are looking for could not be found";
          break;
        default: {
          message =
            error.response && error.response.data
              ? error.response.data["message"]
              : error.message || error;
        }
      }
      return Promise.reject(message);
    }
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
  if (token) axios.defaults.headers.common["Authorization"] = "" + token;
  else delete axios.defaults.headers.common["Authorization"];
};

export const IsImpersonating  = () => {
  return sessionStorage.getItem("Impersonating");
}

export const IsClientFilter  = () => {
  return sessionStorage.getItem(CLIENT_FILTER_KEY);
}

const getUserFromCookie = () => {
  let user = null;
  let is2FAEnabled;
  if(IsImpersonating()){
    user = sessionStorage.getItem(AUTH_SESSION_KEY);
  }else{
    user = localStorage.getItem(AUTH_SESSION_KEY);
    is2FAEnabled = user && user.Is2FaEnabled;
  }
  return user && !is2FAEnabled ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

export const responseHandler = (response: any) => {
    return { Data: response.data.Data, Message: response.data.Message, Error: response.data.Errors || response.data.Message || "Something went wrong, please try again", StatusCode: response.data.StatusCode };
}
class APICore {
  /**
   * Fetches data from given url
   */
  get = async (url: string, params: any, token?:string) => {
    let response;
    if (params && !token) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = await axios.get(`${url}?${queryString}`, params);
    }  else if (token) {
      response = await axios.get(`${url}`, {
        headers: {
          Authorization: `${token}`
        }
      });
    }else if (params && token) {
      response = await axios.get(`${url}?otp=${params.otp}`, {
        headers: {
          Authorization: `${token}`
        }
      });
    } 
   
    else {
      response = await axios.get(`${url}`, params);
    }
    return responseHandler(response);
  };

  getFile = (url: string, params: any) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${url}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = (urls: string, params: any) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`));
    }
    return axios.all(reqs);
  };

  /**
   * post given data to url
   */
  create = async (url: string, data: any) => {
    return responseHandler(await axios.post(url, data))
  };

  /**
   * Updates patch data
   */
  updatePatch = async (url: string, data: any) => {
    return responseHandler(await axios.patch(url, data));
  };

  /**
   * Updates data
   */
  update = async (url: string, data: any) => {
    return responseHandler(await axios.put(url, data));
  };

  /**
   * Deletes data
   */
  delete = async (url: string, data: any = {}) => {
    return responseHandler(await axios.delete(url, { data }));
  };

  /**
   * post given data to url with file
   */
  createWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };

// POST REQUEST for file upload
fileUpload = async (url: string, body: any, config?: any) => {
    const response = await axios.post<any>(url, body, config)
	return responseHandler(response);
};

  /**
   * post given data to url with file
   */
  updateWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.patch(url, formData, config);
  };

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser();
    if (!user) {
      return false;
    }
    const decoded: any = jwtDecode(user.token);
//    console.log('decoded', decoded);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      console.warn("access token expired");
      return false;
    } else {
      return true;
    }
  };

  setLoggedInUser = (session: any) => {
    if (session)
      if(IsImpersonating()){
        sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
      }else{
        localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
      }
    else {
      if(IsImpersonating()){
        sessionStorage.removeItem(AUTH_SESSION_KEY);
      }else{
        localStorage.removeItem(AUTH_SESSION_KEY);
      }
    }
  };
  /**
   * Returns the logged in user
   */
  getLoggedInUser = () => {
    return getUserFromCookie();
  };

  updateLoggedInUser = (data: Record<string, any>) => { 
    const user = getUserFromCookie();
    if (user) {
      if(IsImpersonating()){
        sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({...user, ...data}));
      }else{
        localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({...user, ...data}));
      } 
    }
  };

  setUserInSession = (modifiedUser: any) => {
    let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (userInfo) {
      const { token, user } = JSON.parse(userInfo);
      this.setLoggedInUser({ token, ...user, ...modifiedUser });
    }
  };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
  const { token } = user;
  if (token) {
    setAuthorization(token);
  }
}

export { APICore, setAuthorization };
